<script lang="ts">
	import { onDestroy, onMount, tick } from 'svelte'
	import type { MapViewBlockFieldsFragment } from '$lib/queries/fragments/generated/MapViewBlockFields'
	import type { PinState } from '$lib/stores/block-state'
	import blockState from '$lib/stores/block-state'
	import debounce from 'lodash-es/debounce'
	import createCustomMarkerClass from '$lib/map/CustomMarker'
	import { mapStore, isMapLoaded } from '$lib/stores/map-state'
	import { urlState } from '$lib/stores/url-state'
	import { browser } from '$app/environment'
	import { goto } from '$app/navigation'
	import { HandleBlockClick } from '$lib/utils/mapRoutingState'
	import {
		convertRequirementBlocksToPinState,
		filterBlocksByBounds,
		filterSearchResultsByBounds,
		isBlockHighlighted,
	} from '$lib/utils/map'
	import { memoize } from '$lib/utils/memoize'
	import { BlockType, BlockType as BlockTypeEnum, CustomColor } from '$lib/graphql/enums'
	import { fitBounds } from '$lib/map/map-bounds'
	import { createMarkerComponent, removePins, updateMarkerZOrder } from '$lib/map/map-markers'
	import type { MediaFieldsFragment } from '$src/lib/queries/fragments/generated/MediaFields'

	interface Props {
		markerBlocks?: MapViewBlockFieldsFragment[]
		allBlocks?: MapViewBlockFieldsFragment[]
		isEmptyState?: boolean
	}

	let { markerBlocks = [], allBlocks = [], isEmptyState = false }: Props = $props()

	type CustomMarkerType = InstanceType<ReturnType<typeof createCustomMarkerClass>>

	interface CustomMarkerItem {
		customMarker: CustomMarkerType
		blockId?: string
		component?: any
		isSearchMarker: boolean
		isRequirementsMarker: boolean
		pinState: PinState | null
	}

	let allPins: CustomMarkerItem[] = $state([])
	// Dedicated array for requirements markers to track them separately
	let isFitBoundsRunning = $state(false)

	// Store subscriptions
	let { map, zoom, isMoving, isMobileScreen } = $derived($mapStore)
	let {
		tripId,
		panel: panelState,
		parentBlockId,
		itineraryId,
		isHighlights,
		expandedBlockId,
		isPublic,
		isReview,
		isProfile,
		blockId: selectedBlockId,
		isFullScreen,
	} = $derived($urlState)

	let mapState = $derived($blockState?.mapState)
	let searchResults = $derived($blockState?.searchResults)

	let requirementsResults = $derived.by(() => {
		// Find origin and destination blocks
		const originBlock = allBlocks.find((block) => block.isOrigin === true)
		const destinationBlocks = allBlocks
			.filter((block) => block.isOrigin !== true && block.blockType === BlockType.Destination)
			.sort((a, b) => a.position - b.position)
		const destinationBlock = destinationBlocks.length > 0 ? destinationBlocks[0] : null

		return [originBlock, destinationBlock]
	})

	let shouldHighlight = $derived(isHighlights || selectedBlockId != null || expandedBlockId != null)

	let isSearchMode = $derived(
		mapState === 'search' ||
			isEmptyState ||
			['place', 'pin', 'region', 'destination'].includes(panelState ?? ''),
	)

	let isRequirementsMode = $derived(mapState === 'requirements')

	async function handleInteraction(
		interactionType: string,
		blockId: string | null = null,
		isSearch: boolean = false,
	): Promise<void> {
		if (isProfile) return
		if (interactionType === 'mouseenter') {
			blockState.setHoveringPlaceId(blockId)
			return
		}
		if (interactionType === 'mouseleave') {
			blockState.setHoveringPlaceId(null)
			return
		}

		if (isReview || isProfile) {
			return
		}

		if (isSearch) {
			const block = searchResults?.find((b) => b.id === blockId) ?? null
			if (!block) return

			const location: PinState = {
				lat: block.lat,
				lng: block.lng,
				id: blockId,
			}

			blockState.setSearchSelected(blockId)
			blockState.setSearchResults([location])
			return
		}

		const block = allBlocks.find((b) => b.id === blockId) ?? null
		if (!block) return
		const listingId = block?.trip?.listing?.id

		if (isPublic && listingId && !tripId && selectedBlockId === null) {
			await goto(`/explore/${listingId}`, { keepFocus: false })
			return Promise.resolve()
		}

		if (block?.blockType === BlockTypeEnum.Destination) {
			await openBlockPage(block.id)
			return Promise.resolve()
		} else if (panelState === null) {
			await openBlockPage(block.id)
			return Promise.resolve()
		}
	}

	async function openBlockPage(blockId: string) {
		if (!browser || !tripId) return Promise.resolve()

		await HandleBlockClick(blockId, tripId, null, browser, isPublic, itineraryId)
		return blockState.clearAllCustom()
	}

	// Memoize filter functions for better performance
	const memoizedFilterBlocksByBounds = memoize(filterBlocksByBounds)
	const memoizedFilterSearchResultsByBounds = memoize(filterSearchResultsByBounds)
	const memoizedConvertRequirementsResults = memoize(convertRequirementBlocksToPinState)

	const debouncedCreateMarkers = debounce(updateMarkers, 150, { maxWait: 1000 })

	async function updateMarkers() {
		// Use requestAnimationFrame instead of tick()
		await new Promise((resolve) => requestAnimationFrame(resolve))

		if (
			!map ||
			!window.google ||
			!$isMapLoaded
			// Removed isMoving check to prevent markers not updating during layout transitions
			// Removed isFitBoundsRunning check to increase reliability
			// Keep recenterMap check as we don't want to interfere with that process
		) {
			if ($blockState?.recenterMap == true) return
		}

		try {
			// console.log('Updating markers')
			const CustomMarker = createCustomMarkerClass()
			const unchangedPins: CustomMarkerItem[] = []
			const newPins: CustomMarkerItem[] = []
			const pinsToRemove: CustomMarkerItem[] = []
			// let featuredImagesSet: { rank: number; listingId: string }[] = []

			// Process block markers using memoized filter functions
			let filteredBlocks =
				!isSearchMode && !isRequirementsMode
					? memoizedFilterBlocksByBounds(markerBlocks, map.getBounds(), zoom)
					: []

			const filteredSearchResults =
				isSearchMode && !isRequirementsMode
					? memoizedFilterSearchResultsByBounds(searchResults, map.getBounds())
					: []

			const filteredRequirementsResults = isRequirementsMode
				? memoizedConvertRequirementsResults(requirementsResults)
				: []

			// Create a Set of all valid IDs
			const validIds = new Set([
				...filteredBlocks.map((block) => block.id),
				...(filteredSearchResults?.map((result) => result.id) ?? []),
				...(filteredRequirementsResults?.map((result) => result.id) ?? []),
			])

			// First, identify pins to remove - those not in our valid set
			// When isPublic changes, we need to remove all pins to ensure proper rerendering
			if (isPublic !== prevIsPublic) {
				pinsToRemove.push(...allPins)
			} else {
				allPins.forEach((pin) => {
					if (!validIds.has(pin.blockId)) {
						pinsToRemove.push(pin)
					}
				})
			}

			let featuredImagesSet: {
				rank: number
				listingId: string
				featuredImage: MediaFieldsFragment
			}[] = []
			if (isPublic && tripId) {
				featuredImagesSet = filteredBlocks.slice(0, 10).map((block) => ({
					rank: block.trip?.listing?.rank,
					listingId: block.trip?.listing?.id,
					featuredImage: block.featuredImage,
				}))
			} else if (isPublic) {
				featuredImagesSet = filteredBlocks
					.sort((a, b) => a.trip?.listing?.rank - b.trip?.listing?.rank)
					.filter(
						(block, index, self) =>
							// Only keep first occurrence of each listing ID
							block.trip?.listing?.id &&
							index === self.findIndex((b) => b.trip?.listing?.id === block.trip?.listing?.id),
					)
					.slice(0, 10)
					.map((block) => ({
						rank: block.trip?.listing?.rank,
						listingId: block.trip?.listing?.id,
						featuredImage: block.featuredImage,
					}))
			} else {
				featuredImagesSet = filteredBlocks.slice(0, 10).map((block) => ({
					rank: block.trip?.listing?.rank,
					listingId: block.trip?.listing?.id,
					featuredImage: block.featuredImage,
				}))
			}

			// Process block markers
			if (!isSearchMode && !isRequirementsMode && filteredBlocks?.length > 0) {
				for (const block of filteredBlocks) {
					const existingPin = allPins.find((pin) => pin.blockId === block.id)

					const isActive =
						selectedBlockId === block?.id ||
						parentBlockId === block?.id ||
						expandedBlockId === block?.id

					const isHighlighted = isBlockHighlighted(
						block,
						selectedBlockId,
						parentBlockId,
						expandedBlockId,
						isActive,
						isHighlights,
					)

					// Create new pin
					const featuredImage =
						featuredImagesSet.find((image) => image.featuredImage === block.featuredImage) !==
						undefined
							? block.featuredImage
							: null

					const pinState: PinState = {
						id: block.id,
						lat: block.point.lat,
						lng: block.point.lng,
						color: block.color,
						icon: block.icon,
						blockType: block.blockType,
						isActive: isActive,
						isHighlighted,
						shouldHighlight,
						featuredImage,
					}

					if (existingPin) {
						// Keep existing pin if properties haven't changed
						if (
							existingPin.pinState?.isActive === pinState.isActive &&
							existingPin.pinState?.isHighlighted === pinState.isHighlighted &&
							existingPin.pinState?.isVisible === pinState.isVisible &&
							existingPin?.pinState?.shouldHighlight === pinState.shouldHighlight
						) {
							unchangedPins.push(existingPin)
							continue
						}
						// If properties changed, mark for removal and create new
						pinsToRemove.push(existingPin)
					}

					const { markerElement, markerComponent } = createMarkerComponent(
						pinState,
						isSearchMode,
						handleInteraction,
					)
					const pin = new CustomMarker(markerElement, map, {
						lat: pinState.lat,
						lng: pinState.lng,
					})

					newPins.push({
						customMarker: pin,
						blockId: block.id,
						component: markerComponent,
						isSearchMarker: false,
						isRequirementsMarker: false,
						pinState,
					})
				}
			}

			// Process search markers
			if (isSearchMode && !isRequirementsMode && filteredSearchResults?.length) {
				const isVisible =
					panelState === 'place' ||
					panelState === 'destination' ||
					mapState === 'search' ||
					isEmptyState

				for (const result of filteredSearchResults) {
					const existingPin = allPins.find((pin) => pin.blockId === result.id)

					const pinState: PinState = {
						id: result.id,
						lat: result.lat,
						lng: result.lng,
						color: result.color,
						icon: result.icon,
						blockType: result.blockType,
						isActive: false,
						isHighlighted: false,
						isVisible,
					}

					if (existingPin) {
						// Keep existing pin if properties haven't changed
						if (
							existingPin.pinState?.isActive === pinState.isActive &&
							existingPin.pinState?.isHighlighted === pinState.isHighlighted &&
							existingPin.pinState?.isVisible === pinState.isVisible
						) {
							unchangedPins.push(existingPin)
							continue
						}
						// If properties changed, mark for removal and create new
						pinsToRemove.push(existingPin)
					}

					// Create new pin
					const { markerElement, markerComponent } = createMarkerComponent(
						pinState,
						isSearchMode,
						handleInteraction,
					)
					const pin = new CustomMarker(markerElement, map, {
						lat: pinState.lat,
						lng: pinState.lng,
					})

					newPins.push({
						customMarker: pin,
						blockId: result.id,
						component: markerComponent,
						isSearchMarker: true,
						isRequirementsMarker: false,
						pinState,
					})
				}
			}

			// Process requirements markers
			if (isRequirementsMode && filteredRequirementsResults?.length) {
				for (const result of filteredRequirementsResults) {
					const existingPin = allPins.find((pin) => pin.blockId === result.id)

					const pinState: PinState = result

					if (existingPin) {
						// Keep existing pin if properties haven't changed
						if (
							existingPin.pinState?.isActive === pinState.isActive &&
							existingPin.pinState?.isHighlighted === pinState.isHighlighted &&
							existingPin.pinState?.isVisible === pinState.isVisible &&
							existingPin.pinState?.featuredImage === pinState.featuredImage
						) {
							unchangedPins.push(existingPin)
							continue
						}
						// If properties changed, mark for removal and create new
						pinsToRemove.push(existingPin)
					}

					// Create new pin
					const { markerElement, markerComponent } = createMarkerComponent(
						pinState,
						isSearchMode,
						handleInteraction,
					)
					const pin = new CustomMarker(markerElement, map, {
						lat: pinState.lat,
						lng: pinState.lng,
					})

					newPins.push({
						customMarker: pin,
						blockId: result.id,
						component: markerComponent,
						isSearchMarker: true,
						isRequirementsMarker: true,
						pinState,
					})
				}
			}

			// Remove outdated pins
			await removePins(pinsToRemove)

			// Set base z-index for all pins
			allPins = unchangedPins.concat(newPins).map((pin) => {
				if (pin?.customMarker?.div) {
					pin.customMarker.div.style.zIndex = '1'
				}
				return pin
			})

			// Filter and elevate higher order pins
			const higherOrderPins = allPins?.filter(
				(pin) =>
					pin.pinState?.isHighlighted === true ||
					pin.pinState?.isActive === true ||
					pin.pinState?.featuredImage !== null,
			)

			await tick()
			updateMarkerZOrder(higherOrderPins)
		} catch (error) {
			console.error('Error updating markers:', error)
		} finally {
			// Update previous values
			prevPanelState = panelState
			prevMapState = mapState
			prevParentBlockId = parentBlockId
			prevIsHighlights = isHighlights
			prevExpandedBlockId = expandedBlockId
			prevSelectedBlockId = selectedBlockId
			prevIsSearchMode = isSearchMode
			prevSearchResults = searchResults ? [...searchResults] : []
			prevItineraryId = itineraryId
			previousZoom = zoom
		}
	}

	const MAX_RETRIES = 8
	const DEBOUNCE_WAIT = 150
	const MAX_WAIT = 1000
	const TIMEOUT_MS = 200

	const timeoutPromise = () =>
		new Promise((_, reject) =>
			setTimeout(() => reject(new Error('Fit bounds timeout')), TIMEOUT_MS),
		)

	const withTimeout = async (promise) => {
		return Promise.race([promise, timeoutPromise()])
	}

	let currentBounds: google.maps.LatLngBounds | null = null

	const debouncedFitBounds = debounce(
		async () => {
			isFitBoundsRunning = true
			let retryCount = 0

			try {
				while (retryCount < MAX_RETRIES) {
					try {
						const newBounds = await withTimeout(
							fitBounds(
								allBlocks,
								searchResults,
								isMobileScreen,
								currentBounds,
								requirementsResults,
							),
						)
						if (newBounds) {
							currentBounds = newBounds
							break // Exit if successful
						}
						retryCount++

						if (retryCount < MAX_RETRIES) {
							await new Promise((resolve) => setTimeout(resolve, 100 * retryCount))
						}
					} catch (error) {
						retryCount++
						console.error(`Fit bounds attempt ${retryCount} failed:`, error)

						if (retryCount < MAX_RETRIES) {
							await new Promise((resolve) => setTimeout(resolve, 100 * retryCount))
						}
					}
				}

				if (retryCount >= MAX_RETRIES) {
					console.warn(`Failed to fit bounds after ${MAX_RETRIES} attempts`)
				}
			} finally {
				blockState.setRecenterMap(false)
				isFitBoundsRunning = false
			}
		},
		DEBOUNCE_WAIT,
		{ maxWait: MAX_WAIT },
	)

	// Store previous values for comparison
	let prevPanelState = $state(null)
	let prevMapState = $state(null)
	let prevParentBlockId = $state(null)
	let prevIsHighlights = $state(null)
	let prevExpandedBlockId = $state(null)
	let prevSelectedBlockId = $state(null)
	let prevIsSearchMode = $state(null)
	let prevSearchResults = $state([])
	let prevItineraryId = $state(null)
	let previousZoom = $state(null)
	let prevIsPublic = $state(null)
	let prevIsFullScreen = $state(null)

	let updateTimeout: ReturnType<typeof setTimeout> | null = null

	$effect(() => {
		// Clear any existing timeout
		if (updateTimeout) {
			clearTimeout(updateTimeout)
		}

		// Only proceed if map is ready and not in motion
		if (!$isMapLoaded || !map || isMoving) return

		// Handle recentering map
		if ($blockState?.recenterMap && $blockState.searchSelected == null && !isFitBoundsRunning) {
			debouncedFitBounds()
			return
		}

		// Batch state changes
		const stateSnapshot = {
			panelState,
			mapState,
			parentBlockId,
			isHighlights,
			expandedBlockId,
			selectedBlockId,
			isSearchMode,
			searchResults: searchResults ? [...searchResults] : [],
			itineraryId,
			isPublic,
			zoom,
			isFullScreen,
		}

		// Compare with previous state
		const hasSearchResultsChanged = () => {
			const prev = prevSearchResults
			const curr = stateSnapshot.searchResults

			if (!curr && !prev) return false
			if ((!curr && prev) || (curr && !prev)) return true
			if (curr.length !== prev.length) return true

			return curr.some((result, index) => result.id !== prev[index]?.id)
		}

		const hasChanges =
			stateSnapshot.panelState !== prevPanelState ||
			stateSnapshot.mapState !== prevMapState ||
			stateSnapshot.parentBlockId !== prevParentBlockId ||
			stateSnapshot.isHighlights !== prevIsHighlights ||
			stateSnapshot.expandedBlockId !== prevExpandedBlockId ||
			stateSnapshot.selectedBlockId !== prevSelectedBlockId ||
			stateSnapshot.isSearchMode !== prevIsSearchMode ||
			hasSearchResultsChanged() ||
			stateSnapshot.itineraryId !== prevItineraryId ||
			stateSnapshot.isPublic !== prevIsPublic ||
			stateSnapshot.zoom !== previousZoom ||
			stateSnapshot.isFullScreen !== prevIsFullScreen

		if (hasChanges && !isFitBoundsRunning) {
			// Debounce updates with a timeout instead of lodash debounce
			updateTimeout = setTimeout(() => {
				debouncedCreateMarkers()

				// Update previous values after the markers are created
				prevPanelState = stateSnapshot.panelState
				prevMapState = stateSnapshot.mapState
				prevParentBlockId = stateSnapshot.parentBlockId
				prevIsHighlights = stateSnapshot.isHighlights
				prevExpandedBlockId = stateSnapshot.expandedBlockId
				prevSelectedBlockId = stateSnapshot.selectedBlockId
				prevIsSearchMode = stateSnapshot.isSearchMode
				prevSearchResults = stateSnapshot.searchResults
				prevItineraryId = stateSnapshot.itineraryId
				prevIsPublic = stateSnapshot.isPublic
				previousZoom = stateSnapshot.zoom
				prevIsFullScreen = stateSnapshot.isFullScreen
			}, 150)
		}
	})

	onMount(() => {
		if ($isMapLoaded && map && window.google && !isFitBoundsRunning) {
			updateMarkers()
		}
	})

	onDestroy(() => {
		// Cancel all pending updates
		if (updateTimeout) {
			clearTimeout(updateTimeout)
		}
		debouncedCreateMarkers.cancel()
		debouncedFitBounds.cancel()

		// Clean up all map markers
		void removePins(allPins)
		allPins = []
	})
</script>
